import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AI_RULES } from '../../../constants/queryKeys';
import useAccountPermission from '../../../hooks/useAccountPermission';
import {
  fetchAiRules,
  createAiRule,
  updateAiRule,
  deleteAiRule,
  CreateAiRuleRequest,
  UpdateAiRuleRequest,
} from '../api';

const useChatRules = () => {
  const queryClient = useQueryClient();
  const hasAccountPermission = useAccountPermission();
  const canViewAiRules = hasAccountPermission('VIEW_AI_RULES');

  const invalidateAiRules = () =>
    queryClient.invalidateQueries({
      queryKey: [AI_RULES],
    });

  const handleSuccess = () => {
    invalidateAiRules();
  };

  const aiRulesQuery = useQuery({
    queryKey: [AI_RULES],
    queryFn: () => fetchAiRules(),
    enabled: canViewAiRules,
  });

  const createAiRuleMutation = useMutation({
    mutationFn: (data: CreateAiRuleRequest) => createAiRule(data),
    onSuccess: handleSuccess,
  });

  const updateAiRuleMutation = useMutation({
    mutationFn: ({
      ruleId,
      data,
    }: {
      ruleId: string;
      data: UpdateAiRuleRequest;
    }) => updateAiRule(ruleId, data),
    onSuccess: handleSuccess,
  });

  const deleteAiRuleMutation = useMutation({
    mutationFn: (ruleId: string) => deleteAiRule(ruleId),
    onSuccess: handleSuccess,
  });

  const isLoading =
    aiRulesQuery.isFetching ||
    createAiRuleMutation.isLoading ||
    updateAiRuleMutation.isLoading ||
    deleteAiRuleMutation.isLoading;

  return {
    aiRulesQuery,
    createAiRuleMutation,
    updateAiRuleMutation,
    deleteAiRuleMutation,
    isLoading,
  };
};

export default useChatRules;
